import styled, { css, keyframes } from 'styled-components'
import {
  ANATOMY_COMPACT,
  anatomyCompact,
} from '~/pages/anatomy/libs/responsive'
import { anatomyContainerCss } from '~/pages/anatomy/libs/styles'
import { Button } from '~/shared/components/Button'
import { Container } from '~/shared/components/Container'
import { COLORS } from '~/shared/constants/uiConstants'

const LEVEL_INDICATOR_TARGET = '68%'
export const PROFILE_ACCENT_COLOR = '#7b62ef'

const slideToLevel = keyframes`
  from { inset-inline-start: 0%; }
  to { inset-inline-start: ${LEVEL_INDICATOR_TARGET}; }
`

export const Styles = {
  Root: styled.div`
    width: 100%;
    height: calc(100dvh - var(--header-height));
    display: flex;
    flex-direction: column;
    overflow-y: auto;

    &::before {
      position: absolute;
      z-index: -1;
      inset: 0;
      content: '';
      background-color: ${COLORS.GRAY_100};
    }
  `,

  StyledContainer: styled(Container)`
    ${anatomyContainerCss}
    padding: 24px 16px;
    height: auto;
    flex: 1;
  `,

  Title: styled.h1`
    font-size: 22px;
    line-height: 1;
    font-weight: 700;
    color: ${COLORS.BLACK};
    margin-bottom: 26px;

    ${anatomyCompact} {
      font-size: ${ANATOMY_COMPACT.fontSize.f22};
    }
  `,

  ImageCard: styled.div`
    background: ${COLORS.WHITE};
    border-radius: 30px;
    overflow: hidden;
    margin-bottom: 12px;
  `,

  ImageWrapper: styled.div`
    position: relative;
    width: 100%;
  `,

  ImageHeaderRow: styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    z-index: 1;
    font-size: 14px;
  `,

  AnatomyLabel: styled.span`
    color: ${COLORS.WHITE};
  `,

  HighBadge: styled.span`
    background-color: #05cf2a;
    color: ${COLORS.WHITE};
    line-height: 1;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 5px;
  `,

  ImageTopGradient: styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 76px;
    background: linear-gradient(
      180deg,
      rgba(0, 0, 0, 0.54) 0%,
      rgba(0, 0, 0, 0) 100%
    );
    z-index: 0;
  `,

  ImageBottomGradient: styled.div`
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 76px;
    background: linear-gradient(
      180deg,
      rgba(0, 0, 0, 0) 0%,
      rgba(0, 0, 0, 0.54) 100%
    );
    z-index: 0;
  `,

  CharacterImage: styled.img`
    width: 100%;
    display: block;
  `,

  ProgressSection: styled.div`
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 0 16px 16px;
    z-index: 1;
  `,

  ProgressBar: styled.div`
    height: 12px;
    border-radius: 6px;
    background: linear-gradient(
      90deg,
      #ff0049 0%,
      #ffd800 54.75%,
      #00cf2b 100%
    );
    position: relative;

    html[dir='rtl'] & {
      background: linear-gradient(
        270deg,
        #ff0049 0%,
        #ffd800 54.75%,
        #00cf2b 100%
      );
    }
  `,

  LevelIndicator: styled.div<{ $isVisible: boolean }>`
    position: absolute;
    inset-inline-start: ${LEVEL_INDICATOR_TARGET};
    top: -11px;
    transform: translate(-50%, -50%);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0;
    opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
    transition: opacity 0.3s ease;
    ${({ $isVisible }) =>
      $isVisible
        ? css`
            animation: ${slideToLevel} 1.2s ease-out;
          `
        : ''}
  `,

  LevelPill: styled.div`
    background: ${PROFILE_ACCENT_COLOR};
    color: ${COLORS.WHITE};
    font-size: 10px;
    font-weight: 500;
    padding: 4px 10px;
    border-radius: 12px;
    white-space: nowrap;
  `,

  LevelDot: styled.div`
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: ${PROFILE_ACCENT_COLOR};
    box-shadow: inset 0 0 0 3px ${COLORS.WHITE};
    margin-top: 2px;
  `,

  ProfileCard: styled.div`
    background: ${COLORS.WHITE};
    border-radius: 8px;
    padding: 0 16px;
    margin-bottom: 16px;
  `,

  ProfileItem: styled.div`
    display: flex;
    align-items: flex-start;
    gap: 16px;
    padding: 10px 0;
  `,

  ProfileIcon: styled.div`
    width: 46px;
    height: 46px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 40px;
    flex-shrink: 0;
  `,

  ProfileTextWrapper: styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding-top: 2px;
  `,

  ProfileLabel: styled.span`
    color: #787b7c;
    font-size: 10px;
    line-height: 1;
    font-weight: 500;
  `,

  ProfileValue: styled.span`
    font-size: 14px;
    line-height: 1.2;
    font-weight: 700;
    color: rgba(0, 0, 0, 0.8);
  `,

  StyledButton: styled(Button)`
    margin-top: auto;
  `,
} as const
